import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { ReactComponent as Franchise } from "../../Global Icons/Franchise.svg";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import TextInput from "../../Global Functions/textInput";
import TwoButtons from "../../Global Functions/buttons";
import { viewPage, updatePage } from "../../Redux/Actions/pagesActions";
import { useNavigate, useParams } from "react-router-dom";
import "./../../../src/Components/Reward Voucher/addReward.css";

const TYPE_OPTIONS = [
  { label: "Privacy Policy", value: "privacy_policy" },
  { label: "Terms & Conditions", value: "terms_and_conditions" },
  { label: "About Us", value: "about_us" },
  { label: "Refund Policy", value: "refund_policy" },
];

const MAX_TITLE_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 200;

const EditPages = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { pageDetail, loading } = useSelector((state) => state.pages || {});

  const [formState, setFormState] = useState({
    type: "privacy_policy",
    pdf_url: "",
    description: "",
    pageName: "",
    content: "",
  });

  // fetch existing page
  useEffect(() => {
    if (id) {
      dispatch(viewPage(id));
    }
  }, [dispatch, id]);

  // fill form when data comes
  useEffect(() => {
    const data = pageDetail?.data;
    if (data) {
      setFormState({
        type: data.type || "privacy_policy",
        pdf_url: data.pdf_url || "",
        description: data.description || "",
        pageName: data.pageName || "",
        content: data.content || "",
      });
    }
  }, [pageDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "pageName" && value.length > MAX_TITLE_LENGTH) return;
    if (name === "description" && value.length > MAX_DESCRIPTION_LENGTH) return;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file only.");
      event.target.value = "";
      return;
    }

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Invalid file type. Please upload a .pdf file.");
      event.target.value = "";
      return;
    }

    try {
      const uploadedUrl = await AddImage(file);
      if (uploadedUrl) {
        setFormState((prev) => ({ ...prev, pdf_url: uploadedUrl }));
      }
    } catch {
      toast.error("PDF upload failed");
    }
  };

  const handleSubmit = async () => {
    if (!formState.type) {
      return toast.error("Please select page type.");
    }
    if (!formState.pageName.trim()) {
      return toast.error("Page Name is required.");
    }
    if (!formState.description.trim()) {
      return toast.error("Description is required.");
    }
    if (!formState.content.trim()) {
      return toast.error("Content is required.");
    }
    if (formState.pageName.length > MAX_TITLE_LENGTH) {
      return toast.error(`Page Name cannot exceed ${MAX_TITLE_LENGTH} characters.`);
    }
    if (formState.description.length > MAX_DESCRIPTION_LENGTH) {
      return toast.error(
        `Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters.`
      );
    }
    if (!formState.pdf_url) {
      return toast.error("PDF is required. Please upload a PDF file.");
    }

    const payload = {
      type: formState.type,
      pdf_url: formState.pdf_url,
      description: formState.description,
      pageName: formState.pageName,
      content: formState.content,
    };

    try {
      const res = await dispatch(updatePage(id, payload));
      if (res && (res.statusCode === 200 || res.status === "success")) {
        toast.success("Page updated successfully");
        navigate("/pages");
      } else {
        toast.error(res?.message || "Failed to update page");
      }
    } catch {
      toast.error("Error updating page");
    }
  };

  const getCharCountClass = (length, max) => {
    if (length >= max) return "char-count error";
    if (length >= max * 0.8) return "char-count warning";
    return "char-count safe";
  };

  const getCharCountPercentage = (length, max) =>
    Math.min((length / max) * 100, 100);

  const CharCountDisplay = ({ current, max }) => {
    const percentage = getCharCountPercentage(current, max);
    const statusClass = getCharCountClass(current, max);
    const isWarning = current >= max * 0.8 && current < max;
    const isError = current >= max;

    return (
      <div className={`char-count-container ${statusClass}`}>
        <div className="char-count-bar">
          <div
            className="char-count-fill"
            style={{
              width: `${percentage}%`,
              backgroundColor: isError
                ? "#d9534f"
                : isWarning
                ? "#f0ad4e"
                : "#5cb85c",
            }}
          />
        </div>
        <div className="char-count-text">
          <span className="char-count-current">{current}</span>
          <span className="char-count-separator">/</span>
          <span className="char-count-max">{max}</span>
          {isWarning && !isError && <span className="char-count-warning-icon">⚠️</span>}
          {isError && <span className="char-count-error-icon">✕</span>}
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="franchise-form-container">
        <div className="franchise-form-wrapper franchise-card">
          <div className="franchise-card-content">
            <div className="franchise-header">
              <div className="franchise-header-icon">
                <Franchise />
              </div>
              <h1>Edit Page</h1>
            </div>

            {/* Type */}
            <div className="form-section">
              <label className="form-label" htmlFor="type_select">
                Type <span style={{ color: "red" }}>*</span>
              </label>
              <select
                id="type_select"
                name="type"
                value={formState.type}
                onChange={handleChange}
                className="form-input"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Page Name */}
            <div className="form-section">
              <TextInput
                label="Page Name"
                name="pageName"
                value={formState.pageName}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
                maxLength={MAX_TITLE_LENGTH}
              />
              <CharCountDisplay
                current={formState.pageName.length}
                max={MAX_TITLE_LENGTH}
              />
            </div>

            {/* Description */}
            <div className="form-section">
              <TextInput
                label="Description"
                name="description"
                value={formState.description}
                onChange={handleChange}
                required
                inputClass="form-input"
                labelClass="form-label"
                maxLength={MAX_DESCRIPTION_LENGTH}
              />
              <CharCountDisplay
                current={formState.description.length}
                max={MAX_DESCRIPTION_LENGTH}
              />
            </div>

            {/* Content */}
            <div className="form-section">
              <label className="form-label" htmlFor="content_input">
                Content <span style={{ color: "red" }}>*</span>
              </label>
              <textarea
                id="content_input"
                name="content"
                value={formState.content}
                onChange={handleChange}
                className="form-input"
                rows={6}
                style={{ width: "96.8%" }}
              />
            </div>

            {/* PDF */}
            <div className="form-section">
              <label className="form-label" htmlFor="pdf_input">
                PDF <span style={{ color: "red" }}>*</span>
              </label>
              <input
                id="pdf_input"
                style={{ width: "97%" }}
                type="file"
                name="pdf"
                accept="application/pdf"
                onChange={handleFileChange}
                className="form-input"
              />
              {formState.pdf_url && (
                <a
                  href={formState.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "block", marginTop: 8, color: "Blue" }}
                >
                  View uploaded PDF
                </a>
              )}
            </div>

            <div className="form-navigation">
              <button type="button" onClick={() => navigate("/pages")}>
                Cancel
              </button>
              <TwoButtons
                onClick2={handleSubmit}
                buttonHead2={loading ? "Saving..." : "Save"}
              />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditPages;
